import type {
  ActPdfInput,
  ActSuggest,
  ApiTokenStatus,
  AuditFilters,
  AuditLogList,
  AuditOptions,
  Batch,
  BulkAssignResult,
  CreateAntennasResult,
  CreateUserInput,
  Customer,
  CurrentUser,
  CustomerStatDetail,
  CustomersStatsResponse,
  DeliveryPlanRow,
  DeliveryType,
  Device,
  DeviceHistory,
  DeviceLookup,
  DeviceLookupResult,
  DocInfo,
  NotificationListResponse,
  NotificationPreference,
  ProcurementAttachmentKind,
  PurchaseOrder,
  PurchaseOrderAttachment,
  PurchaseOrderDetail,
  PurchaseOrderLine,
  PurchaseOrderStatus,
  PurchaseOrderTracking,
  ReturnBox,
  ReturnBoxAttachment,
  ReturnBoxDetail,
  ReturnBoxDeviceRow,
  RmaListEntry,
  RmaStatus,
  SavedDeviceLookup,
  StockItem,
  StockIssue,
  StockIssueChange,
  StockIssueKind,
  Technician,
  IssueRecipient,
  HoldingRow,
  CreateStockIssueInput,
  IssueRecipientType,
  ProductType,
  AntennaImportResult,
  ImportMappingRequest,
  ImportPreview,
  RecipeLine,
  ConsumptionRow,
  StockMovement,
  ScanSessionFull,
  ScanSessionSummary,
  ShipmentStatsResponse,
  PeriodStats,
  StatsOverview,
  Supplier,
  ExpenseCategory,
  Expense,
  ExpenseAttachment,
  ExpenseDetail,
  DriveFolder,
  DriveSpreadsheet,
  DriveStatus,
  SheetExportResult,
  FirmwareFile,
  FirmwareUploadResult,
  FlashEvent,
  FlashHistoryEvent,
  FlashQueueDevice,
  GodexJobsPayloadResult,
  GodexPayloadResult,
  GodexPrintResult,
  GodexStatus,
  ImportResult,
  LabelBackground,
  LabelTemplate,
  LabelType,
  PassportCandidate,
  Shipment,
  ShipmentAttachment,
  UpdateUserInput,
  UploadDriveResult,
  TelegramLinkStartResponse,
  TelegramTestResponse,
  User,
  Worker,
  WorkEntry,
  WorkerPayment,
  WorkerRole,
  WorkersReportSummary,
} from "./types";

export type { CurrentUser, Role } from "./types";

const API_BASE = "/api/v1";

export class ApiError extends Error {
  constructor(public status: number, public detail: unknown) {
    super(`API error ${status}`);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    ...init,
  });

  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      // ignore
    }
    throw new ApiError(res.status, detail);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function requestForm<T>(path: string, body: FormData): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    body,
  });
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      // ignore
    }
    throw new ApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

export const api = {
  // ── auth
  async login(username: string, password: string): Promise<CurrentUser> {
    const data = await request<{ user: CurrentUser }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
    return data.user;
  },
  async logout(): Promise<void> {
    await request<{ ok: true }>("/auth/logout", { method: "POST" });
  },
  async me(): Promise<CurrentUser> {
    const data = await request<{ user: CurrentUser }>("/auth/me");
    return data.user;
  },
  async health(): Promise<{ status: string; db: boolean }> {
    return request("/health");
  },
  async stats(): Promise<StatsOverview> {
    return request("/stats");
  },
  async statsPeriod(from: string, to: string): Promise<PeriodStats> {
    const params = new URLSearchParams({ from, to });
    return request(`/stats/period?${params.toString()}`);
  },
  async customersStats(): Promise<CustomersStatsResponse> {
    return request("/stats/customers");
  },
  async customerStatDetail(id: number): Promise<CustomerStatDetail> {
    return request(`/stats/customers/${id}`);
  },

  // ── customers
  async listCustomers(): Promise<Customer[]> {
    const data = await request<{ customers: Customer[] }>("/customers");
    return data.customers;
  },
  async getCustomer(id: number): Promise<Customer> {
    const data = await request<{ customer: Customer }>(`/customers/${id}`);
    return data.customer;
  },
  async createCustomer(payload: Partial<Customer>): Promise<Customer> {
    const data = await request<{ customer: Customer }>("/customers", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return data.customer;
  },
  async updateCustomer(id: number, payload: Partial<Customer>): Promise<Customer> {
    const data = await request<{ customer: Customer }>(`/customers/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
    return data.customer;
  },

  // ── batches
  async listBatches(opts: { q?: string } = {}): Promise<Batch[]> {
    const qs = opts.q ? `?q=${encodeURIComponent(opts.q)}` : "";
    const data = await request<{ batches: Batch[] }>(`/batches${qs}`);
    return data.batches;
  },
  async getBatch(id: number): Promise<Batch> {
    const data = await request<{ batch: Batch }>(`/batches/${id}`);
    return data.batch;
  },
  async listBatchDevices(id: number): Promise<Device[]> {
    const data = await request<{ devices: Device[] }>(`/batches/${id}/devices`);
    return data.devices;
  },
  async createBatch(payload: Partial<Batch>): Promise<Batch> {
    const data = await request<{ batch: Batch }>("/batches", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return data.batch;
  },
  async updateBatch(id: number, payload: Partial<Batch>): Promise<Batch> {
    const data = await request<{ batch: Batch }>(`/batches/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
    return data.batch;
  },
  async deleteBatch(id: number, force = false): Promise<{ deleted: boolean; device_count: number }> {
    return request(`/batches/${id}${force ? "?force=1" : ""}`, { method: "DELETE" });
  },
  async importBatchXlsx(id: number, file: File, mapped?: ImportMappingRequest): Promise<ImportResult> {
    const fd = new FormData();
    fd.append("file", file);
    if (mapped) {
      fd.append("mapping", JSON.stringify(mapped.mapping));
      fd.append("header_row", String(mapped.header_row));
      if (mapped.sheet) fd.append("sheet", mapped.sheet);
      if (mapped.join_eui_default) fd.append("join_eui_default", mapped.join_eui_default);
      if (mapped.app_key_default) fd.append("app_key_default", mapped.app_key_default);
    }
    return requestForm<ImportResult>(`/batches/${id}/import-xlsx`, fd);
  },
  async previewBatchImport(
    id: number,
    file: File,
    opts: { sheet?: string; header_row?: number } = {},
  ): Promise<ImportPreview> {
    const fd = new FormData();
    fd.append("file", file);
    if (opts.sheet) fd.append("sheet", opts.sheet);
    if (opts.header_row !== undefined) fd.append("header_row", String(opts.header_row));
    return requestForm<ImportPreview>(`/batches/${id}/import-xlsx/preview`, fd);
  },
  async importBatchAntennas(id: number, input: { text: string } | File): Promise<AntennaImportResult> {
    if (input instanceof File) {
      const fd = new FormData();
      fd.append("file", input);
      return requestForm<AntennaImportResult>(`/batches/${id}/import-antennas`, fd);
    }
    return request<AntennaImportResult>(`/batches/${id}/import-antennas`, {
      method: "POST",
      body: JSON.stringify({ text: input.text }),
    });
  },
  async importBatchPaste(id: number, text: string): Promise<ImportResult> {
    return request<ImportResult>(`/batches/${id}/import-paste`, {
      method: "POST",
      body: JSON.stringify({ text }),
    });
  },

  // ── shipments
  async listShipments(opts: { q?: string } = {}): Promise<Shipment[]> {
    const qs = opts.q ? `?q=${encodeURIComponent(opts.q)}` : "";
    const data = await request<{ shipments: Shipment[] }>(`/shipments${qs}`);
    return data.shipments;
  },
  async getShipment(id: number): Promise<Shipment> {
    const data = await request<{ shipment: Shipment }>(`/shipments/${id}`);
    return data.shipment;
  },
  async listShipmentAttachments(shipmentId: number): Promise<ShipmentAttachment[]> {
    const r = await request<{ attachments: ShipmentAttachment[] }>(`/shipments/${shipmentId}/attachments`);
    return r.attachments;
  },
  async uploadShipmentAttachment(shipmentId: number, file: File, note?: string): Promise<ShipmentAttachment> {
    const fd = new FormData();
    fd.append("file", file);
    if (note) fd.append("note", note);
    const r = await requestForm<{ attachment: ShipmentAttachment }>(`/shipments/${shipmentId}/attachments`, fd);
    return r.attachment;
  },
  shipmentAttachmentDownloadUrl(shipmentId: number, attachmentId: number): string {
    return `${API_BASE}/shipments/${shipmentId}/attachments/${attachmentId}`;
  },
  async deleteShipmentAttachment(shipmentId: number, attachmentId: number): Promise<void> {
    await request(`/shipments/${shipmentId}/attachments/${attachmentId}`, { method: "DELETE" });
  },
  async listShipmentDevices(id: number): Promise<Device[]> {
    const data = await request<{ devices: Device[] }>(`/shipments/${id}/devices`);
    return data.devices;
  },
  async getShipmentBoxCount(
    id: number,
    boxNumber: string,
  ): Promise<{ count: number; by_type?: Record<string, number> }> {
    return request<{ count: number; by_type?: Record<string, number> }>(
      `/shipments/${id}/box-count?box=${encodeURIComponent(boxNumber)}`,
    );
  },
  async nextShipmentCode(): Promise<{ code: string }> {
    return request(`/shipments/next-code`);
  },
  async createShipment(
    payload: Partial<Shipment> & { batch_id?: number },
  ): Promise<{ shipment: Shipment; assigned_devices: number }> {
    return request("/shipments", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  async updateShipment(id: number, payload: Partial<Shipment>): Promise<Shipment> {
    const data = await request<{ shipment: Shipment }>(`/shipments/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
    return data.shipment;
  },
  async deleteShipment(id: number): Promise<{ deleted: boolean; detached_devices: number }> {
    return request(`/shipments/${id}`, { method: "DELETE" });
  },
  async listDeliveryTypes(active?: boolean): Promise<DeliveryType[]> {
    const qs = active === undefined ? "" : `?active=${active}`;
    const r = await request<{ delivery_types: DeliveryType[] }>(`/shipments/delivery-types${qs}`);
    return r.delivery_types;
  },
  async createDeliveryType(name: string, marks_debt = false): Promise<DeliveryType> {
    const r = await request<{ delivery_type: DeliveryType }>(`/shipments/delivery-types`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, marks_debt }),
    });
    return r.delivery_type;
  },
  async updateDeliveryType(id: number, patch: { name?: string; marks_debt?: boolean; active?: boolean }): Promise<DeliveryType> {
    const r = await request<{ delivery_type: DeliveryType }>(`/shipments/delivery-types/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    return r.delivery_type;
  },
  async deleteDeliveryType(id: number): Promise<void> {
    await request(`/shipments/delivery-types/${id}`, { method: "DELETE" });
  },
  async setShipmentBoxes(
    id: number,
    updates: { device_id: number; box_number: string }[],
  ): Promise<{ updated: number }> {
    return request(`/shipments/${id}/devices/box`, {
      method: "POST",
      body: JSON.stringify({ updates }),
    });
  },
  async bulkAssign(
    shipmentId: number,
    boxNumber: string,
    queries: string[],
    opts?: { fromReturn?: boolean },
  ): Promise<BulkAssignResult> {
    return request(`/shipments/${shipmentId}/devices/bulk-assign`, {
      method: "POST",
      body: JSON.stringify({ box_number: boxNumber, queries, from_return: opts?.fromReturn }),
    });
  },
  async createAntennas(
    shipmentId: number,
    devEuis: string[],
    boxNumber: string | null,
  ): Promise<CreateAntennasResult> {
    return request(`/shipments/${shipmentId}/devices/create-antennas`, {
      method: "POST",
      body: JSON.stringify({ dev_euis: devEuis, box_number: boxNumber }),
    });
  },
  async importShipmentText(
    shipmentId: number,
    text: string,
    boxNumber?: string,
  ): Promise<BulkAssignResult & { queries_total: number }> {
    return request(`/shipments/${shipmentId}/devices/import-text`, {
      method: "POST",
      body: JSON.stringify({ text, box_number: boxNumber || undefined }),
    });
  },
  async distributeBoxes(
    shipmentId: number,
    rules: { count: number; box_number: string }[],
    onlyUnboxed: boolean,
  ): Promise<{
    applied: number;
    by_box: Record<string, number>;
    leftover_rule_capacity: number;
    leftover_devices: number;
    eligible_total: number;
  }> {
    return request(`/shipments/${shipmentId}/devices/distribute-boxes`, {
      method: "POST",
      body: JSON.stringify({ rules, only_unboxed: onlyUnboxed }),
    });
  },
  async importShipmentFile(
    shipmentId: number,
    file: File,
    boxNumber?: string,
  ): Promise<BulkAssignResult & { queries_total: number }> {
    const fd = new FormData();
    fd.append("file", file);
    if (boxNumber) fd.append("box_number", boxNumber);
    return requestForm<BulkAssignResult & { queries_total: number }>(
      `/shipments/${shipmentId}/devices/import-file`,
      fd,
    );
  },
  exportShipmentUrl(id: number, opts: { accounting?: boolean } = {}): string {
    const qs = opts.accounting ? "?accounting=1" : "";
    return `${API_BASE}/shipments/${id}/export${qs}`;
  },
  exportShipmentsCombinedUrl(ids: number[], opts: { accounting?: boolean } = {}): string {
    const base = `${API_BASE}/shipments/export-combined?ids=${ids.join(",")}`;
    return opts.accounting === false ? `${base}&accounting=0` : base;
  },
  exportWarrantyUrl(ids: number[]): string {
    return `${API_BASE}/shipments/export-warranty?ids=${ids.join(",")}`;
  },
  exportWarrantyPdfUrl(ids: number[]): string {
    return `${API_BASE}/shipments/export-warranty-pdf?ids=${ids.join(",")}`;
  },
  async markShipmentsDebt(ids: number[], onDebt: boolean): Promise<{ updated: number }> {
    return request(`/shipments/mark-debt`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids, on_debt: onDebt }),
    });
  },
  async bulkChirpstackDone(ids: number[]): Promise<{ updated: number }> {
    return request(`/shipments/bulk-chirpstack-done`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    });
  },
  async bulkAccountingConfirm(ids: number[]): Promise<{ updated: number }> {
    return request(`/shipments/bulk-accounting-confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    });
  },
  async bulkChirpstackUndo(ids: number[]): Promise<{ updated: number }> {
    return request(`/shipments/bulk-chirpstack-undo`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    });
  },
  async bulkAccountingUndo(ids: number[]): Promise<{ updated: number }> {
    return request(`/shipments/bulk-accounting-undo`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    });
  },
  async bulkSetStatus(ids: number[], status: string): Promise<{ updated: number }> {
    return request(`/shipments/bulk-status`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids, status }),
    });
  },
  async bulkSetCustomer(ids: number[], customerId: number | null): Promise<{ updated: number }> {
    return request(`/shipments/bulk-customer`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids, customer_id: customerId }),
    });
  },
  async listDeliveryPlans(
    year: number,
    customerId?: number,
    productTypeId: number = 1,
  ): Promise<{ year: number; rows: DeliveryPlanRow[] }> {
    const p = new URLSearchParams({ year: String(year), product_type_id: String(productTypeId) });
    if (customerId != null) p.set("customer_id", String(customerId));
    return request(`/shipments/delivery-plans?${p.toString()}`);
  },
  async upsertDeliveryPlan(body: {
    customer_id: number;
    period_month: string;
    planned_qty: number;
    product_type_id?: number;
  }): Promise<{ plan: { id: number; customer_id: number; period_month: string; planned_qty: number } }> {
    return request(`/shipments/delivery-plans`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  async deleteDeliveryPlan(planId: number): Promise<{ ok: true }> {
    return request(`/shipments/delivery-plans/${planId}`, { method: "DELETE" });
  },
  exportShipmentQrGroupsUrl(
    id: number,
    opts: { chunk_size?: number; use_model?: boolean; box_number?: string } = {},
  ): string {
    const params = new URLSearchParams();
    if (opts.chunk_size != null) params.set("chunk_size", String(opts.chunk_size));
    if (opts.use_model != null) params.set("use_model", opts.use_model ? "1" : "0");
    if (opts.box_number) params.set("box_number", opts.box_number);
    const qs = params.toString();
    return `${API_BASE}/shipments/${id}/export-qr-groups${qs ? `?${qs}` : ""}`;
  },
  async uploadShipmentToDrive(id: number, folder_id?: string): Promise<UploadDriveResult> {
    return request(`/shipments/${id}/upload-to-drive`, {
      method: "POST",
      body: JSON.stringify(folder_id ? { folder_id } : {}),
    });
  },

  // ── devices (scanner)
  async lookupDevice(query: string): Promise<DeviceLookupResult> {
    return request(`/devices/lookup?q=${encodeURIComponent(query)}`);
  },
  async scanDecode(imageDataUrl: string): Promise<{
    results: Array<{
      data: string;
      polygon: [number, number][];
      bbox: { x: number; y: number; width: number; height: number };
    }>;
    frame?: { width: number; height: number };
  }> {
    return request(`/scan/decode`, {
      method: "POST",
      body: JSON.stringify({ image: imageDataUrl }),
    });
  },
  async scanDecodeBlob(blob: Blob, signal?: AbortSignal): Promise<{
    results: Array<{
      data: string;
      polygon: [number, number][];
      bbox: { x: number; y: number; width: number; height: number };
    }>;
    frame?: { width: number; height: number };
  }> {
    const res = await fetch(`${API_BASE}/scan/decode`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": blob.type || "image/jpeg" },
      body: blob,
      signal,
    });
    if (!res.ok) {
      let detail: unknown = null;
      try {
        detail = await res.json();
      } catch {
        // ignore
      }
      throw new ApiError(res.status, detail);
    }
    return await res.json();
  },
  async createScanSession(payload: {
    name: string;
    mode?: string | null;
    shipment_id?: number | null;
    box_number?: string | null;
    logs: unknown[];
  }): Promise<ScanSessionFull> {
    return request<ScanSessionFull>("/scan/sessions", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  async listScanSessions(limit = 50): Promise<ScanSessionSummary[]> {
    const data = await request<{ sessions: ScanSessionSummary[] }>(
      `/scan/sessions?limit=${limit}`,
    );
    return data.sessions;
  },
  async getScanSession(id: number): Promise<ScanSessionFull> {
    return request<ScanSessionFull>(`/scan/sessions/${id}`);
  },
  async deleteScanSession(id: number): Promise<{ ok: true }> {
    return request<{ ok: true }>(`/scan/sessions/${id}`, { method: "DELETE" });
  },
  async updateDevice(deviceId: number, updates: Partial<Device> & { notes?: string | null; linked_dev_eui?: string | null }): Promise<{ device: Device; changes: Record<string, unknown> }> {
    return request(`/devices/${deviceId}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  },
  async bulkUpdateDevices(
    deviceIds: number[],
    updates: Record<string, unknown>,
  ): Promise<{ updated: number; not_found: number[]; total: number }> {
    return request(`/devices/bulk-update`, {
      method: "POST",
      body: JSON.stringify({ device_ids: deviceIds, updates }),
    });
  },
  async bulkDeleteDevices(deviceIds: number[]): Promise<{ deleted: number }> {
    return request(`/devices`, {
      method: "DELETE",
      body: JSON.stringify({ device_ids: deviceIds }),
    });
  },
  async deviceHistory(deviceId: number): Promise<DeviceHistory> {
    return request(`/devices/${deviceId}/history`);
  },
  async listSavedLookups(): Promise<SavedDeviceLookup[]> {
    const res = await request<{ saved: SavedDeviceLookup[] }>("/devices/saved-lookups");
    return res.saved;
  },
  async saveDeviceLookup(device_id: number, note?: string | null): Promise<SavedDeviceLookup> {
    const body = JSON.stringify({ device_id, note: note ?? null });
    const res = await request<{ saved: SavedDeviceLookup }>("/devices/saved-lookups", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
    });
    return res.saved;
  },
  async unsaveDeviceLookup(device_id: number): Promise<void> {
    await request(`/devices/saved-lookups/${device_id}`, { method: "DELETE" });
  },
  async listDevicesByStatus(
    statuses: string[],
    limit = 200,
    opts: { unassigned?: boolean } = {},
  ): Promise<{ devices: RmaListEntry[]; count: number }> {
    const params = new URLSearchParams();
    params.set("status", statuses.join(","));
    params.set("limit", String(limit));
    if (opts.unassigned) params.set("unassigned", "1");
    return request(`/devices?${params.toString()}`);
  },
  async searchDevices(
    q: string,
    limit = 20,
  ): Promise<{ devices: RmaListEntry[]; count: number }> {
    const params = new URLSearchParams({ q, limit: String(limit) });
    return request(`/devices/search?${params.toString()}`);
  },
  async exportDevices(
    deviceIds: number[],
    format: "xlsx" | "csv",
  ): Promise<{ blob: Blob; filename: string }> {
    const res = await fetch(`${API_BASE}/devices/export`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ device_ids: deviceIds, format }),
    });
    if (!res.ok) {
      let detail: unknown = null;
      try {
        detail = await res.json();
      } catch {
        // ignore
      }
      throw new ApiError(res.status, detail);
    }
    const blob = await res.blob();
    const cd = res.headers.get("Content-Disposition") || "";
    const m = cd.match(/filename="([^"]+)"/);
    const filename = m ? m[1] : `devices_export.${format}`;
    return { blob, filename };
  },
  async markDeviceReturned(
    deviceId: number,
    status: RmaStatus,
    reason: string,
  ): Promise<{ device: Device; previous_status: string }> {
    return request(`/devices/${deviceId}/return`, {
      method: "POST",
      body: JSON.stringify({ status, reason }),
    });
  },
  async markDeviceScanned(
    deviceId: number,
    payload: {
      box_number?: string;
      shipment_id?: number;
      advance_status?: boolean;
      force?: boolean;
      from_return?: boolean;
    } = {},
  ): Promise<{ device: Device }> {
    return request(`/devices/${deviceId}/scan`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  // ── labels: templates ──────────────────────────────────────────────────
  async listLabelTemplates(): Promise<Record<LabelType, LabelTemplate>> {
    const data = await request<{ templates: Record<LabelType, LabelTemplate> }>(
      "/labels/templates",
    );
    return data.templates;
  },
  async getLabelTemplate(type: LabelType): Promise<LabelTemplate> {
    const data = await request<{ template: LabelTemplate }>(`/labels/templates/${type}`);
    return data.template;
  },
  async updateLabelTemplate(
    type: LabelType,
    patch: Partial<LabelTemplate>,
  ): Promise<LabelTemplate> {
    const data = await request<{ template: LabelTemplate }>(
      `/labels/templates/${type}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    );
    return data.template;
  },
  async resetLabelTemplate(type: LabelType): Promise<LabelTemplate> {
    const data = await request<{ template: LabelTemplate }>(
      `/labels/templates/${type}/reset`,
      { method: "POST" },
    );
    return data.template;
  },

  // ── labels: backgrounds ────────────────────────────────────────────────
  async listLabelBackgrounds(
    type: LabelType,
  ): Promise<{ backgrounds: LabelBackground[]; default_background_id: string | null }> {
    return request(`/labels/templates/${type}/backgrounds`);
  },
  async uploadLabelBackground(
    type: LabelType,
    file: File,
    opts: { name?: string; fit?: string; threshold?: number } = {},
  ): Promise<LabelBackground> {
    const fd = new FormData();
    fd.append("file", file);
    if (opts.name) fd.append("name", opts.name);
    if (opts.fit) fd.append("fit", opts.fit);
    if (opts.threshold != null) fd.append("threshold", String(opts.threshold));
    const data = await requestForm<{ background: LabelBackground }>(
      `/labels/templates/${type}/backgrounds`,
      fd,
    );
    return data.background;
  },
  async updateLabelBackground(
    type: LabelType,
    bgId: string,
    patch: { name?: string; fit?: string; threshold?: number },
  ): Promise<LabelBackground> {
    const data = await request<{ background: LabelBackground }>(
      `/labels/templates/${type}/backgrounds/${bgId}`,
      { method: "PATCH", body: JSON.stringify(patch) },
    );
    return data.background;
  },
  async deleteLabelBackground(type: LabelType, bgId: string): Promise<void> {
    await request(`/labels/templates/${type}/backgrounds/${bgId}`, { method: "DELETE" });
  },
  labelBackgroundUrl(type: LabelType, bgId: string): string {
    return `/api/v1/labels/templates/${type}/backgrounds/${bgId}/image`;
  },

  // ── godex
  async godexStatus(): Promise<GodexStatus> {
    return request("/godex/status");
  },
  async godexPrintTest(background = "default"): Promise<GodexPrintResult> {
    return request(`/godex/print-test?background=${encodeURIComponent(background)}`,
                   { method: "POST", body: "{}" });
  },
  async godexPrintDevice(
    deviceId: number, lot_code?: string, background = "default",
  ): Promise<GodexPrintResult> {
    return request(
      `/godex/print-device/${deviceId}?background=${encodeURIComponent(background)}`,
      { method: "POST", body: JSON.stringify(lot_code ? { lot_code } : {}) },
    );
  },
  async godexPrintBox(
    shipmentId: number, boxNumber: string, background = "default",
  ): Promise<GodexPrintResult> {
    return request(`/godex/print-box?background=${encodeURIComponent(background)}`, {
      method: "POST",
      body: JSON.stringify({ shipment_id: shipmentId, box_number: boxNumber }),
    });
  },
  async godexPrintShipmentBatches(
    shipmentId: number,
    opts: { chunk_size: number; box_number?: string; use_model?: boolean },
  ): Promise<GodexPrintResult & { labels?: number; devices?: number }> {
    return request(`/godex/print-shipment-batches/${shipmentId}`, {
      method: "POST",
      body: JSON.stringify(opts),
    });
  },
  async godexPrintShipment(
    shipmentId: number,
    size: "small" | "large" = "small",
    background = "default",
  ): Promise<GodexPrintResult> {
    return request(
      `/godex/print-shipment/${shipmentId}?background=${encodeURIComponent(background)}`,
      { method: "POST", body: JSON.stringify({ size }) },
    );
  },
  async godexPreviewShipmentEzpl(
    shipmentId: number,
    size: "small" | "large" = "small",
  ): Promise<{ size: string; code: string; bytes: number; ezpl: string }> {
    return request(`/godex/print-shipment/${shipmentId}/preview?size=${size}`);
  },
  async godexPrintRaw(ezpl: string): Promise<GodexPrintResult> {
    return request(`/godex/print-raw`, {
      method: "POST",
      body: JSON.stringify({ ezpl }),
    });
  },
  async godexPrintTestPayload(): Promise<GodexPayloadResult> {
    return request<GodexPayloadResult>("/godex/print-test?payload_only=1", { method: "POST", body: "{}" });
  },
  async godexPrintDevicePayload(deviceId: number, lot_code?: string): Promise<GodexPayloadResult> {
    return request<GodexPayloadResult>(`/godex/print-device/${deviceId}?payload_only=1`, {
      method: "POST",
      body: JSON.stringify(lot_code ? { lot_code } : {}),
    });
  },
  async godexPrintBoxPayload(shipmentId: number, boxNumber: string): Promise<GodexPayloadResult> {
    return request<GodexPayloadResult>("/godex/print-box?payload_only=1", {
      method: "POST",
      body: JSON.stringify({ shipment_id: shipmentId, box_number: boxNumber }),
    });
  },
  async godexPrintShipmentPayload(shipmentId: number, size: "small" | "large" = "small"): Promise<GodexPayloadResult> {
    return request<GodexPayloadResult>(`/godex/print-shipment/${shipmentId}?payload_only=1`, {
      method: "POST",
      body: JSON.stringify({ size }),
    });
  },
  async godexPrintLink(shipmentId: number, size: "small" | "large" = "small"): Promise<GodexPrintResult> {
    return request(`/godex/print-link/${shipmentId}`, { method: "POST", body: JSON.stringify({ size }) });
  },
  async godexPrintLinkPayload(shipmentId: number, size: "small" | "large" = "small"): Promise<GodexPayloadResult> {
    return request(`/godex/print-link/${shipmentId}?payload_only=1`, { method: "POST", body: JSON.stringify({ size }) });
  },
  async ensurePublicLink(shipmentId: number, regenerate = false): Promise<{ public_token: string; public_url: string }> {
    return request(`/shipments/${shipmentId}/public-link${regenerate ? "?regenerate=1" : ""}`, { method: "POST" });
  },
  async revokePublicLink(shipmentId: number): Promise<{ revoked: boolean }> {
    return request(`/shipments/${shipmentId}/public-link`, { method: "DELETE" });
  },
  async godexPrintShipmentBatchesPayload(
    shipmentId: number,
    opts: { chunk_size: number; box_number?: string; use_model?: boolean },
  ): Promise<GodexJobsPayloadResult> {
    return request<GodexJobsPayloadResult>(`/godex/print-shipment-batches/${shipmentId}?payload_only=1`, {
      method: "POST",
      body: JSON.stringify(opts),
    });
  },

  async godexPrintReturnBox(
    boxId: number,
    size: "small" | "large" = "small",
    background = "default",
  ): Promise<GodexPrintResult> {
    return request(`/godex/print-return-box/${boxId}?background=${encodeURIComponent(background)}`, {
      method: "POST",
      body: JSON.stringify({ size }),
    });
  },
  async godexPrintReturnBoxPayload(boxId: number, size: "small" | "large" = "small"): Promise<GodexPayloadResult> {
    return request<GodexPayloadResult>(`/godex/print-return-box/${boxId}?payload_only=1`, {
      method: "POST",
      body: JSON.stringify({ size }),
    });
  },
  async godexPrintStockItem(itemId: number, copies = 1, background = "default"): Promise<GodexPrintResult> {
    return request(`/godex/print-stock-item/${itemId}?background=${encodeURIComponent(background)}`, {
      method: "POST",
      body: JSON.stringify({ copies }),
    });
  },
  async godexPrintStockItemPayload(itemId: number, copies = 1): Promise<GodexPayloadResult> {
    return request<GodexPayloadResult>(`/godex/print-stock-item/${itemId}?payload_only=1`, {
      method: "POST",
      body: JSON.stringify({ copies }),
    });
  },
  async returnBoxByCode(code: string): Promise<{ id: number; code: string }> {
    return request(`/return-boxes/by-code/${encodeURIComponent(code)}`);
  },

  async listPassportCandidates(params: {
    shipment_id?: number; from?: string; to?: string; q?: string;
  }): Promise<{ devices: PassportCandidate[]; count: number }> {
    const p = new URLSearchParams();
    if (params.shipment_id != null) p.set("shipment_id", String(params.shipment_id));
    if (params.from) p.set("from", params.from);
    if (params.to) p.set("to", params.to);
    if (params.q) p.set("q", params.q);
    return request(`/devices/passport-candidates?${p.toString()}`);
  },
  async godexPrintPassportsPayload(deviceIds: number[], size: "small" | "large" = "small", delayMs?: number, kinds?: string[]): Promise<GodexJobsPayloadResult> {
    return request<GodexJobsPayloadResult>(`/godex/print-passports?payload_only=1`, {
      method: "POST",
      body: JSON.stringify({ device_ids: deviceIds, size, delay_ms: delayMs, kinds }),
    });
  },
  async godexPrintPassports(deviceIds: number[], size: "small" | "large" = "small", delayMs?: number, kinds?: string[]): Promise<{ printed: boolean; labels_sent: number; labels_total: number; error?: string | null }> {
    return request(`/godex/print-passports`, {
      method: "POST",
      body: JSON.stringify({ device_ids: deviceIds, size, delay_ms: delayMs, kinds }),
    });
  },

  // ── firmware
  async listBatchFirmware(batchId: number): Promise<FirmwareFile[]> {
    const data = await request<{ firmware: FirmwareFile[] }>(`/batches/${batchId}/firmware`);
    return data.firmware;
  },
  async uploadBatchFirmware(batchId: number, files: File[]): Promise<FirmwareUploadResult> {
    const fd = new FormData();
    for (const f of files) fd.append("file", f);
    return requestForm<FirmwareUploadResult>(`/batches/${batchId}/firmware`, fd);
  },
  async deleteBatchFirmware(batchId: number, filename: string): Promise<void> {
    await request<{ deleted: true }>(
      `/batches/${batchId}/firmware/${encodeURIComponent(filename)}`,
      { method: "DELETE" },
    );
  },
  async deleteAllBatchFirmware(batchId: number): Promise<number> {
    const res = await request<{ deleted: true; count: number }>(
      `/batches/${batchId}/firmware`,
      { method: "DELETE" },
    );
    return res.count;
  },
  async listFirmwareAllBatches(excludeBatchId?: number): Promise<{
    batch_id: number;
    batch_code: string;
    files: FirmwareFile[];
  }[]> {
    const qs = excludeBatchId !== undefined ? `?exclude_batch=${excludeBatchId}` : "";
    const res = await request<{ batches: { batch_id: number; batch_code: string; files: FirmwareFile[] }[] }>(
      `/firmware/all-batches${qs}`,
    );
    return res.batches;
  },
  async importFirmwareFromBatch(
    destBatchId: number,
    sourceBatchId: number,
    filenames: string[],
  ): Promise<{ copied: string[]; errors: { filename: string; error: string }[] }> {
    return await request<{ copied: string[]; errors: { filename: string; error: string }[] }>(
      `/batches/${destBatchId}/firmware/import`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source_batch_id: sourceBatchId, filenames }),
      },
    );
  },
  async listFlashQueue(batchId: number): Promise<FlashQueueDevice[]> {
    const data = await request<{ devices: FlashQueueDevice[] }>(
      `/batches/${batchId}/flash-queue`,
    );
    return data.devices;
  },
  async lookupDeviceBySerial(
    batchId: number,
    serial: string,
    signal?: AbortSignal,
  ): Promise<DeviceLookup> {
    const data = await request<{ device: DeviceLookup }>(
      `/batches/${batchId}/device-by-serial?serial=${encodeURIComponent(serial)}`,
      { signal },
    );
    return data.device;
  },
  async listFlashHistory(batchId: number, limit = 50): Promise<FlashHistoryEvent[]> {
    const data = await request<{ events: FlashHistoryEvent[] }>(
      `/batches/${batchId}/flash-history?limit=${limit}`,
    );
    return data.events;
  },
  async downloadFirmware(batchId: number, filename: string): Promise<string> {
    const res = await fetch(`${API_BASE}/firmware/download/${batchId}/${encodeURIComponent(filename)}`, {
      credentials: "include",
    });
    if (!res.ok) throw new ApiError(res.status, null);
    const buf = await res.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let bin = "";
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin);
  },
  async reportFlashResult(
    deviceId: number,
    success: boolean,
    returncode: number | null,
    firmware: string,
  ): Promise<void> {
    await request<{ updated: boolean }>(`/devices/${deviceId}/flash-result`, {
      method: "POST",
      body: JSON.stringify({ success, returncode, firmware }),
    });
  },
  async flashDevice(
    deviceId: number,
    onEvent: (event: FlashEvent) => void,
    firmwareFilename?: string,
  ): Promise<void> {
    const res = await fetch(`${API_BASE}/devices/${deviceId}/flash`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(firmwareFilename ? { firmware_filename: firmwareFilename } : {}),
    });
    if (!res.ok || !res.body) {
      let detail: unknown = null;
      try {
        detail = await res.json();
      } catch {
        // ignore
      }
      throw new ApiError(res.status, detail);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buf.indexOf("\n\n")) >= 0) {
        const block = buf.slice(0, idx).trim();
        buf = buf.slice(idx + 2);
        if (!block) continue;
        const payload = block.startsWith("data:") ? block.slice(5).trim() : block;
        try {
          onEvent(JSON.parse(payload) as FlashEvent);
        } catch {
          // skip malformed event
        }
      }
    }
  },

  // ── audit log (admin only)
  async listAuditLogs(filters: AuditFilters = {}): Promise<AuditLogList> {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
      if (v !== undefined && v !== null && v !== "") params.append(k, String(v));
    }
    const qs = params.toString();
    return request<AuditLogList>(`/audit-logs${qs ? `?${qs}` : ""}`);
  },
  async auditOptions(): Promise<AuditOptions> {
    return request<AuditOptions>("/audit-logs/actions");
  },

  // ── documents (passport + DoC)
  passportUrl(): string {
    return `${API_BASE}/documents/passport`;
  },
  passportA4Url(): string {
    return `${API_BASE}/documents/passport-a4`;
  },
  docUrl(): string {
    return `${API_BASE}/documents/doc`;
  },
  async docInfo(): Promise<DocInfo> {
    return request<DocInfo>("/documents/doc/info");
  },
  async uploadDoc(file: File): Promise<DocInfo & { uploaded: true }> {
    const fd = new FormData();
    fd.append("file", file);
    return requestForm<DocInfo & { uploaded: true }>("/documents/doc", fd);
  },
  async deleteDoc(): Promise<{ deleted: boolean }> {
    return request("/documents/doc", { method: "DELETE" });
  },

  // ── drive
  async driveStatus(): Promise<DriveStatus> {
    return request("/drive/status");
  },
  async driveConnect(): Promise<{ auth_url: string }> {
    return request("/drive/connect", { method: "POST" });
  },
  async driveDisconnect(): Promise<DriveStatus> {
    return request("/drive/disconnect", { method: "POST" });
  },
  async driveFolders(query?: string): Promise<DriveFolder[]> {
    const qs = query ? `?q=${encodeURIComponent(query)}` : "";
    const data = await request<{ folders: DriveFolder[] }>(`/drive/folders${qs}`);
    return data.folders;
  },
  async listDriveSpreadsheets(query?: string): Promise<DriveSpreadsheet[]> {
    const qs = query ? `?q=${encodeURIComponent(query)}` : "";
    const r = await request<{ spreadsheets: DriveSpreadsheet[] }>(`/drive/spreadsheets${qs}`);
    return r.spreadsheets;
  },
  async sheetTabs(target?: "shipments" | "defects"): Promise<{ spreadsheet_name: string; tabs: string[] }> {
    const qs = target ? `?target=${target}` : "";
    return request(`/shipments/sheet-tabs${qs}`);
  },
  async exportToSheet(body: {
    shipment_ids: number[];
    tab: string;
    dev_euis?: string[];
    force?: boolean;
  }): Promise<SheetExportResult> {
    return request(`/shipments/export-to-sheet`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  async sendAttachmentsToAccounting(attachmentIds: number[]): Promise<{ queued: number }> {
    return request(`/shipments/attachments/send-to-accounting`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attachment_ids: attachmentIds }),
    });
  },
  async exportToSheetGroups(body: {
    shipment_ids: number[];
    tab: string;
    send_activation?: boolean;
    send_attachments?: boolean;
  }): Promise<{
    activation?: { text: string; link: string | null } | null;
    attachments?: { photos: number; documents: number } | null;
    activation_sent?: boolean;
    attachments_sent?: boolean;
  }> {
    return request(`/shipments/export-to-sheet/groups`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  async exportDevicesToSheet(body: {
    device_ids: number[];
    tab: string;
    dev_euis?: string[];
    force?: boolean;
  }): Promise<SheetExportResult> {
    return request(`/devices/export-to-sheet`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  async exportBoxToSheet(body: {
    box_id: number;
    filter: "all" | "repaired" | "defect";
    tab: string;
    dev_euis?: string[];
    force?: boolean;
  }): Promise<SheetExportResult> {
    return request(`/return-boxes/${body.box_id}/export-to-sheet`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tab: body.tab, filter: body.filter, dev_euis: body.dev_euis, force: body.force }),
    });
  },
  async exportAllReturnsToSheet(body: {
    tab: string;
    dev_euis?: string[];
    force?: boolean;
  }): Promise<SheetExportResult> {
    return request(`/return-boxes/export-all-to-sheet`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },

  // ── users
  async listUsers(): Promise<User[]> {
    const data = await request<{ users: User[] }>("/users");
    return data.users;
  },
  async createUser(input: CreateUserInput): Promise<User> {
    const data = await request<{ user: User }>("/users", {
      method: "POST",
      body: JSON.stringify(input),
    });
    return data.user;
  },
  async updateUser(id: number, patch: UpdateUserInput): Promise<User> {
    const data = await request<{ user: User }>(`/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
    return data.user;
  },
  async deleteUser(id: number): Promise<void> {
    await request(`/users/${id}`, { method: "DELETE" });
  },
  async updateMyProfile(patch: { email: string }): Promise<CurrentUser> {
    const data = await request<{ user: CurrentUser }>("/users/me", {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
    return data.user;
  },
  async changeMyPassword(current_password: string, new_password: string): Promise<void> {
    await request("/users/me/password", {
      method: "POST",
      body: JSON.stringify({ current_password, new_password }),
    });
  },
  async getApiTokenStatus(): Promise<ApiTokenStatus> {
    return request<ApiTokenStatus>("/users/me/api-token-status");
  },
  async generateApiToken(): Promise<{ token: string }> {
    return request<{ token: string }>("/users/me/api-token", { method: "POST" });
  },
  async deleteApiToken(): Promise<void> {
    await request("/users/me/api-token", { method: "DELETE" });
  },

  // ── branding
  logoUrl(version?: number): string {
    return version != null ? `/api/v1/branding/logo?v=${version}` : `/api/v1/branding/logo`;
  },
  async uploadLogo(file: File): Promise<{ ok: true; ext: string }> {
    const fd = new FormData();
    fd.append("file", file);
    return requestForm<{ ok: true; ext: string }>("/branding/logo", fd);
  },
  async resetLogo(): Promise<void> {
    await request("/branding/logo", { method: "DELETE" });
  },

  // ── exports
  async suggestAct(shipmentIds: number[]): Promise<ActSuggest> {
    return request<ActSuggest>("/exports/act-suggest", {
      method: "POST",
      body: JSON.stringify({ shipment_ids: shipmentIds }),
    });
  },
  async generateActPdf(input: ActPdfInput): Promise<Blob> {
    const res = await fetch(`${API_BASE}/exports/act-pdf`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    if (!res.ok) {
      let detail: unknown = null;
      try { detail = await res.json(); } catch {}
      throw new ApiError(res.status, detail);
    }
    return res.blob();
  },

  // ── Worker roles ──────────────────────────────────────────────────────────
  async listWorkerRoles(active?: boolean): Promise<WorkerRole[]> {
    const qs = active === undefined ? "" : `?active=${active ? "true" : "false"}`;
    const res = await request<{ roles: WorkerRole[] }>(`/worker-roles${qs}`);
    return res.roles;
  },
  async createWorkerRole(name: string): Promise<WorkerRole> {
    const res = await request<{ role: WorkerRole }>("/worker-roles", {
      method: "POST",
      body: JSON.stringify({ name }),
    });
    return res.role;
  },
  async updateWorkerRole(id: number, patch: Partial<Pick<WorkerRole, "name" | "active">>): Promise<WorkerRole> {
    const res = await request<{ role: WorkerRole }>(`/worker-roles/${id}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    });
    return res.role;
  },
  async deleteWorkerRole(id: number): Promise<void> {
    await request(`/worker-roles/${id}`, { method: "DELETE" });
  },

  // ── Workers ──────────────────────────────────────────────────────────────
  async listWorkers(active?: boolean): Promise<Worker[]> {
    const qs = active === undefined ? "" : `?active=${active ? "true" : "false"}`;
    const res = await request<{ workers: Worker[] }>(`/workers${qs}`);
    return res.workers;
  },
  async getWorker(id: number): Promise<Worker> {
    const res = await request<{ worker: Worker }>(`/workers/${id}`);
    return res.worker;
  },
  async createWorker(body: {
    full_name: string;
    phone?: string | null;
    role_id?: number | null;
    daily_rate?: number | null;
    notes?: string | null;
  }): Promise<Worker> {
    const res = await request<{ worker: Worker }>("/workers", {
      method: "POST",
      body: JSON.stringify(body),
    });
    return res.worker;
  },
  async updateWorker(id: number, patch: Partial<Omit<Worker, "id" | "created_at" | "updated_at">>): Promise<Worker> {
    const res = await request<{ worker: Worker }>(`/workers/${id}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    });
    return res.worker;
  },
  async deleteWorker(id: number): Promise<void> {
    await request(`/workers/${id}`, { method: "DELETE" });
  },

  // ── Work entries ─────────────────────────────────────────────────────────
  async listWorkEntries(params: { from?: string; to?: string; worker_id?: number } = {}): Promise<WorkEntry[]> {
    const qs = new URLSearchParams();
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.worker_id !== undefined) qs.set("worker_id", String(params.worker_id));
    const s = qs.toString();
    const res = await request<{ entries: WorkEntry[] }>(`/work-entries${s ? "?" + s : ""}`);
    return res.entries;
  },
  async createWorkEntry(body: {
    worker_id: number;
    work_date: string;
    start_time: string;
    end_time: string;
    amount?: number | null;
    note?: string | null;
  }): Promise<WorkEntry> {
    const res = await request<{ entry: WorkEntry }>("/work-entries", {
      method: "POST",
      body: JSON.stringify(body),
    });
    return res.entry;
  },
  async updateWorkEntry(id: number, patch: Partial<Pick<WorkEntry, "work_date" | "start_time" | "end_time" | "amount" | "note">>): Promise<WorkEntry> {
    const res = await request<{ entry: WorkEntry }>(`/work-entries/${id}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    });
    return res.entry;
  },
  async deleteWorkEntry(id: number): Promise<void> {
    await request(`/work-entries/${id}`, { method: "DELETE" });
  },

  // ── Worker payments ──────────────────────────────────────────────────────
  async listWorkerPayments(params: { from?: string; to?: string; worker_id?: number } = {}): Promise<WorkerPayment[]> {
    const qs = new URLSearchParams();
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.worker_id !== undefined) qs.set("worker_id", String(params.worker_id));
    const s = qs.toString();
    const res = await request<{ payments: WorkerPayment[] }>(`/worker-payments${s ? "?" + s : ""}`);
    return res.payments;
  },
  async createWorkerPayment(body: {
    worker_id: number;
    paid_at: string;
    amount: number;
    covers_from?: string | null;
    covers_to?: string | null;
    note?: string | null;
  }): Promise<WorkerPayment> {
    const res = await request<{ payment: WorkerPayment }>("/worker-payments", {
      method: "POST",
      body: JSON.stringify(body),
    });
    return res.payment;
  },
  async updateWorkerPayment(id: number, patch: Partial<Pick<WorkerPayment, "paid_at" | "amount" | "covers_from" | "covers_to" | "note">>): Promise<WorkerPayment> {
    const res = await request<{ payment: WorkerPayment }>(`/worker-payments/${id}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    });
    return res.payment;
  },
  async deleteWorkerPayment(id: number): Promise<void> {
    await request(`/worker-payments/${id}`, { method: "DELETE" });
  },

  // ── Reports ──────────────────────────────────────────────────────────────
  async workersReportSummary(from: string, to: string): Promise<WorkersReportSummary> {
    return await request<WorkersReportSummary>(`/workers/report/summary?from=${from}&to=${to}`);
  },
  workersReportExportUrl(from: string, to: string): string {
    return `/api/v1/workers/report/export?from=${from}&to=${to}&format=xlsx`;
  },

  // ── Shipment stats ───────────────────────────────────────────────────────
  async shipmentStats(params: {
    from: string;
    to: string;
    date_field?: "shipped" | "created";
    customer_id?: "all" | "null" | number;
  }): Promise<ShipmentStatsResponse> {
    const qs = new URLSearchParams();
    qs.set("from", params.from);
    qs.set("to", params.to);
    if (params.date_field) qs.set("date_field", params.date_field);
    if (params.customer_id !== undefined) qs.set("customer_id", String(params.customer_id));
    return await request<ShipmentStatsResponse>(`/shipments/stats?${qs.toString()}`);
  },
  shipmentStatsExportUrl(params: {
    from: string;
    to: string;
    date_field?: "shipped" | "created";
    customer_id?: "all" | "null" | number;
  }): string {
    const qs = new URLSearchParams();
    qs.set("from", params.from);
    qs.set("to", params.to);
    if (params.date_field) qs.set("date_field", params.date_field);
    if (params.customer_id !== undefined) qs.set("customer_id", String(params.customer_id));
    return `/api/v1/shipments/stats/export?${qs.toString()}`;
  },

  // ── Suppliers ────────────────────────────────────────────────────────────
  async listSuppliers(active?: boolean): Promise<Supplier[]> {
    const qs = active === undefined ? "" : `?active=${active ? "true" : "false"}`;
    const r = await request<{ suppliers: Supplier[] }>(`/procurement/suppliers${qs}`);
    return r.suppliers;
  },
  async createSupplier(body: { name: string; url?: string | null; contact?: string | null }): Promise<Supplier> {
    const r = await request<{ supplier: Supplier }>("/procurement/suppliers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return r.supplier;
  },
  async updateSupplier(id: number, patch: Partial<Pick<Supplier, "name" | "url" | "contact" | "active">>): Promise<Supplier> {
    const r = await request<{ supplier: Supplier }>(`/procurement/suppliers/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    return r.supplier;
  },
  async deleteSupplier(id: number): Promise<void> {
    await request(`/procurement/suppliers/${id}`, { method: "DELETE" });
  },

  // ── Expense categories & expenses ────────────────────────────────────────
  async listExpenseCategories(active?: boolean): Promise<ExpenseCategory[]> {
    const qs = active === undefined ? "" : `?active=${active}`;
    const r = await request<{ categories: ExpenseCategory[] }>(`/procurement/expense-categories${qs}`);
    return r.categories;
  },
  async createExpenseCategory(name: string): Promise<ExpenseCategory> {
    const r = await request<{ category: ExpenseCategory }>(`/procurement/expense-categories`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name }),
    });
    return r.category;
  },
  async updateExpenseCategory(id: number, patch: { name?: string; active?: boolean }): Promise<ExpenseCategory> {
    const r = await request<{ category: ExpenseCategory }>(`/procurement/expense-categories/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    return r.category;
  },
  async deleteExpenseCategory(id: number): Promise<void> {
    await request(`/procurement/expense-categories/${id}`, { method: "DELETE" });
  },
  async listExpenses(params: { category_id?: number; from?: string; to?: string; method?: string } = {}): Promise<Expense[]> {
    const qs = new URLSearchParams();
    if (params.category_id != null) qs.set("category_id", String(params.category_id));
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    if (params.method) qs.set("method", params.method);
    const s = qs.toString();
    const r = await request<{ expenses: Expense[] }>(`/procurement/expenses${s ? `?${s}` : ""}`);
    return r.expenses;
  },
  async getExpense(id: number): Promise<ExpenseDetail> {
    return request(`/procurement/expenses/${id}`);
  },
  async createExpense(body: {
    spent_at: string; category_id: number; amount: string; currency: string;
    description?: string; payment_method?: string; paid_by?: string;
  }): Promise<Expense> {
    const r = await request<{ expense: Expense }>(`/procurement/expenses`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return r.expense;
  },
  async updateExpense(id: number, patch: Record<string, unknown>): Promise<Expense> {
    const r = await request<{ expense: Expense }>(`/procurement/expenses/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    return r.expense;
  },
  async deleteExpense(id: number): Promise<void> {
    await request(`/procurement/expenses/${id}`, { method: "DELETE" });
  },
  async uploadExpenseAttachment(expenseId: number, file: File, note?: string): Promise<ExpenseAttachment> {
    const fd = new FormData();
    fd.append("file", file);
    if (note) fd.append("note", note);
    const r = await requestForm<{ attachment: ExpenseAttachment }>(`/procurement/expenses/${expenseId}/attachments`, fd);
    return r.attachment;
  },
  expenseAttachmentDownloadUrl(expenseId: number, attachmentId: number): string {
    return `${API_BASE}/procurement/expenses/${expenseId}/attachments/${attachmentId}`;
  },
  async deleteExpenseAttachment(expenseId: number, attachmentId: number): Promise<void> {
    await request(`/procurement/expenses/${expenseId}/attachments/${attachmentId}`, { method: "DELETE" });
  },

  // ── Purchase orders ──────────────────────────────────────────────────────
  async listOrders(params: { status?: PurchaseOrderStatus[]; supplier_id?: number; from?: string; to?: string } = {}): Promise<PurchaseOrder[]> {
    const qs = new URLSearchParams();
    (params.status ?? []).forEach((s) => qs.append("status", s));
    if (params.supplier_id !== undefined) qs.set("supplier_id", String(params.supplier_id));
    if (params.from) qs.set("from", params.from);
    if (params.to) qs.set("to", params.to);
    const s = qs.toString();
    const r = await request<{ orders: PurchaseOrder[] }>(`/procurement/orders${s ? "?" + s : ""}`);
    return r.orders;
  },
  async getOrder(id: number): Promise<PurchaseOrderDetail> {
    return await request<PurchaseOrderDetail>(`/procurement/orders/${id}`);
  },
  async createOrder(body: {
    supplier_id: number;
    ordered_at: string;
    title?: string | null;
    supplier_order_id?: string | null;
    received_at?: string | null;
    total_amount?: number | null;
    total_currency?: string | null;
    status?: PurchaseOrderStatus;
    notes?: string | null;
  }): Promise<PurchaseOrder> {
    const r = await request<{ order: PurchaseOrder }>("/procurement/orders", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return r.order;
  },
  async updateOrder(id: number, patch: Partial<Omit<PurchaseOrder, "id" | "code" | "created_at" | "updated_at" | "created_by_id">>): Promise<PurchaseOrder> {
    const r = await request<{ order: PurchaseOrder }>(`/procurement/orders/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    return r.order;
  },
  async deleteOrder(id: number): Promise<void> {
    await request(`/procurement/orders/${id}`, { method: "DELETE" });
  },

  // ── Order lines ──────────────────────────────────────────────────────────
  async createOrderLine(orderId: number, body: { code?: string | null; description?: string | null; quantity: number; unit_price?: number | null }): Promise<PurchaseOrderLine> {
    const r = await request<{ line: PurchaseOrderLine }>(`/procurement/orders/${orderId}/lines`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return r.line;
  },
  async updateOrderLine(orderId: number, lineId: number, patch: Partial<Pick<PurchaseOrderLine, "code" | "description" | "quantity" | "unit_price">>): Promise<PurchaseOrderLine> {
    const r = await request<{ line: PurchaseOrderLine }>(`/procurement/orders/${orderId}/lines/${lineId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    return r.line;
  },
  async deleteOrderLine(orderId: number, lineId: number): Promise<void> {
    await request(`/procurement/orders/${orderId}/lines/${lineId}`, { method: "DELETE" });
  },

  // ── Attachments ──────────────────────────────────────────────────────────
  async uploadOrderAttachment(orderId: number, file: File, kind: ProcurementAttachmentKind, note?: string | null): Promise<PurchaseOrderAttachment> {
    const form = new FormData();
    form.append("file", file);
    form.append("kind", kind);
    if (note) form.append("note", note);
    const r = await requestForm<{ attachment: PurchaseOrderAttachment }>(`/procurement/orders/${orderId}/attachments`, form);
    return r.attachment;
  },
  orderAttachmentDownloadUrl(orderId: number, attachmentId: number): string {
    return `/api/v1/procurement/orders/${orderId}/attachments/${attachmentId}`;
  },
  orderAttachmentsZipUrl(orderId: number): string {
    return `/api/v1/procurement/orders/${orderId}/attachments/zip`;
  },
  async deleteOrderAttachment(orderId: number, attachmentId: number): Promise<void> {
    await request(`/procurement/orders/${orderId}/attachments/${attachmentId}`, { method: "DELETE" });
  },

  // ── Trackings ────────────────────────────────────────────────────────────
  async createOrderTracking(orderId: number, body: { tracking_number: string; carrier_code?: number | null; note?: string | null }): Promise<PurchaseOrderTracking> {
    const r = await request<{ tracking: PurchaseOrderTracking }>(`/procurement/orders/${orderId}/trackings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return r.tracking;
  },
  async refreshOrderTracking(orderId: number, trackingId: number): Promise<PurchaseOrderTracking> {
    const r = await request<{ tracking: PurchaseOrderTracking }>(`/procurement/orders/${orderId}/trackings/${trackingId}/refresh`, {
      method: "POST",
    });
    return r.tracking;
  },
  async deleteOrderTracking(orderId: number, trackingId: number): Promise<void> {
    await request(`/procurement/orders/${orderId}/trackings/${trackingId}`, { method: "DELETE" });
  },

  // ── Generic settings ─────────────────────────────────────────────────────
  async getSetting(key: string): Promise<{ key: string; set: boolean; value?: unknown }> {
    return await request<{ key: string; set: boolean; value?: unknown }>(`/settings/${key}`);
  },
  async putSetting(key: string, value: unknown): Promise<{ key: string; set: true }> {
    return await request<{ key: string; set: true }>(`/settings/${key}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ value }),
    });
  },
  getKnownGroups: () =>
    request<{ key: string; set: boolean; value?: { chat_id: number; title: string; seen_at?: string }[] }>(
      "/settings/telegram.known_groups",
    ),
  async testTrack17Key(): Promise<{ ok: boolean; info?: unknown; error?: string }> {
    return await request<{ ok: boolean; info?: unknown; error?: string }>("/settings/track17.api_key/test", {
      method: "POST",
    });
  },
  async telegramLinkStart(): Promise<TelegramLinkStartResponse> {
    return await request<TelegramLinkStartResponse>("/telegram/link/start", { method: "POST" });
  },
  async telegramLinkCancel(): Promise<void> {
    await request("/telegram/link/cancel", { method: "POST" });
  },
  async telegramDisconnect(): Promise<void> {
    await request("/telegram/link", { method: "DELETE" });
  },
  async telegramTest(): Promise<TelegramTestResponse> {
    return await request<TelegramTestResponse>("/settings/telegram/test", { method: "POST" });
  },

  // ── Return boxes (RMA triage) ──────────────────────────────────────────────
  async listReturnBoxes(): Promise<ReturnBox[]> {
    const r = await request<{ boxes: ReturnBox[] }>("/return-boxes");
    return r.boxes;
  },
  async createReturnBox(body: { customer_id?: number | null; received_at?: string | null; notes?: string | null }): Promise<ReturnBox> {
    const r = await request<{ box: ReturnBox }>("/return-boxes", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return r.box;
  },
  async getReturnBox(id: number): Promise<ReturnBoxDetail> {
    return await request<ReturnBoxDetail>(`/return-boxes/${id}`);
  },
  async updateReturnBox(id: number, patch: { customer_id?: number | null; received_at?: string | null; notes?: string | null; act_number?: string | null; predator_note?: string | null }): Promise<ReturnBox> {
    const r = await request<{ box: ReturnBox }>(`/return-boxes/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    return r.box;
  },
  async deleteReturnBox(id: number): Promise<void> {
    await request(`/return-boxes/${id}`, { method: "DELETE" });
  },
  async scanIntoReturnBox(id: number, query: string): Promise<{ device: ReturnBoxDeviceRow; moved_from_box_id: number | null; already_here: boolean }> {
    return await request<{ device: ReturnBoxDeviceRow; moved_from_box_id: number | null; already_here: boolean }>(
      `/return-boxes/${id}/scan`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query }),
      },
    );
  },
  async scanTriageReturnBox(
    boxId: number,
    query: string,
    action: "restore" | "defect",
  ): Promise<{ device: ReturnBoxDeviceRow; action: string }> {
    return await request<{ device: ReturnBoxDeviceRow; action: string }>(
      `/return-boxes/${boxId}/scan-triage`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query, action }),
      },
    );
  },
  async restoreInReturnBox(boxId: number, deviceId: number): Promise<void> {
    await request(`/return-boxes/${boxId}/devices/${deviceId}/restore`, { method: "POST" });
  },
  async defectInReturnBox(boxId: number, deviceId: number): Promise<void> {
    await request(`/return-boxes/${boxId}/devices/${deviceId}/defect`, { method: "POST" });
  },
  async removeFromReturnBox(boxId: number, deviceId: number): Promise<void> {
    await request(`/return-boxes/${boxId}/devices/${deviceId}`, { method: "DELETE" });
  },
  async restoreAllInReturnBox(boxId: number): Promise<{ restored: number }> {
    return request(`/return-boxes/${boxId}/restore-all`, { method: "POST" });
  },
  async defectAllInReturnBox(boxId: number): Promise<{ defect: number }> {
    return request(`/return-boxes/${boxId}/defect-all`, { method: "POST" });
  },
  async transferReturnBoxToShipment(boxId: number): Promise<{ shipment: Shipment; transferred: number }> {
    return request(`/return-boxes/${boxId}/transfer-to-shipment`, { method: "POST" });
  },
  returnBoxActPdfUrl(boxId: number): string {
    return `${API_BASE}/return-boxes/${boxId}/act.pdf`;
  },
  returnBoxExportUrl(
    boxId: number,
    format: "xlsx" | "pdf",
    filter: "all" | "repaired" | "defect",
    xlsxFormat?: "full",
  ): string {
    const extra = xlsxFormat ? `&format=${xlsxFormat}` : "";
    return `${API_BASE}/return-boxes/${boxId}/export.${format}?filter=${filter}${extra}`;
  },

  async listReturnBoxAttachments(boxId: number): Promise<ReturnBoxAttachment[]> {
    const r = await request<{ attachments: ReturnBoxAttachment[] }>(`/return-boxes/${boxId}/attachments`);
    return r.attachments;
  },
  async uploadReturnBoxAttachment(boxId: number, file: File, note?: string): Promise<ReturnBoxAttachment> {
    const fd = new FormData();
    fd.append("file", file);
    if (note) fd.append("note", note);
    const r = await requestForm<{ attachment: ReturnBoxAttachment }>(`/return-boxes/${boxId}/attachments`, fd);
    return r.attachment;
  },
  returnBoxAttachmentDownloadUrl(boxId: number, attachmentId: number): string {
    return `${API_BASE}/return-boxes/${boxId}/attachments/${attachmentId}`;
  },
  async deleteReturnBoxAttachment(boxId: number, attachmentId: number): Promise<void> {
    await request(`/return-boxes/${boxId}/attachments/${attachmentId}`, { method: "DELETE" });
  },

  addForeignModule: (boxId: number, code: string) =>
    request<{ module: import("./types").ForeignModule; created: boolean }>(
      `/return-boxes/${boxId}/foreign`,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ code }) },
    ),
  deleteForeignModule: (boxId: number, fid: number) =>
    request<{ ok: boolean }>(`/return-boxes/${boxId}/foreign/${fid}`, { method: "DELETE" }),

  async bulkAssignToReturnBox(
    boxId: number,
    deviceIds: number[],
  ): Promise<{
    assigned: number[];
    failed: { id: number; error: string }[];
    box: ReturnBoxDetail["box"];
  }> {
    return await request(`/return-boxes/${boxId}/bulk-assign`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ device_ids: deviceIds }),
    });
  },

  async revertDevicesFromRma(
    ids: number[],
  ): Promise<{
    reverted: { id: number; from: string; to: string }[];
    failed: { id: number; error: string }[];
  }> {
    return await request("/devices/rma/revert", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    });
  },

  async scrapDevices(
    ids: number[],
  ): Promise<{
    scrapped: { id: number; from: string }[];
    failed: { id: number; error: string }[];
  }> {
    return await request("/devices/rma/scrap", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    });
  },

  // ── Stock (consumables) ───────────────────────────────────────────────────
  async listStock(): Promise<{ items: StockItem[] }> {
    return request(`/stock`);
  },
  async createStock(body: Partial<StockItem>): Promise<{ item: StockItem }> {
    return request(`/stock`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  async updateStock(id: number, body: Partial<StockItem>): Promise<{ item: StockItem }> {
    return request(`/stock/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },
  async deleteStock(id: number): Promise<{ ok: boolean }> {
    return request(`/stock/${id}`, { method: "DELETE" });
  },
  async restockStock(id: number, qty: number, note?: string): Promise<{ item: StockItem }> {
    return request(`/stock/${id}/restock`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ qty, note }),
    });
  },
  async adjustStock(id: number, quantity: number, note?: string): Promise<{ item: StockItem }> {
    return request(`/stock/${id}/adjust`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ quantity, note }),
    });
  },
  async stockMovements(id: number, limit = 50): Promise<{ movements: StockMovement[] }> {
    return request(`/stock/${id}/movements?limit=${limit}`);
  },
  async recomputeStock(): Promise<{ fixed: number }> {
    return request(`/stock/recompute`, { method: "POST" });
  },
  async listProductTypes(): Promise<{ types: ProductType[] }> {
    return request(`/product-types`);
  },
  async createProductType(name: string): Promise<{ type: ProductType }> {
    return request(`/product-types`, { method: "POST", body: JSON.stringify({ name }) });
  },
  async updateProductType(id: number, body: Partial<Pick<ProductType, "name" | "active" | "sort_order">>): Promise<{ type: ProductType }> {
    return request(`/product-types/${id}`, { method: "PUT", body: JSON.stringify(body) });
  },
  async deleteProductType(id: number): Promise<{ ok: boolean }> {
    return request(`/product-types/${id}`, { method: "DELETE" });
  },
  async saveRecipe(id: number, lines: Pick<RecipeLine, "stock_item_id" | "qty" | "moment">[], confirm = false): Promise<{ type: ProductType }> {
    return request(`/product-types/${id}/recipe`, { method: "PUT", body: JSON.stringify(confirm ? { lines, confirm: true } : { lines }) });
  },
  async stockConsumption(params: { from: string; to: string; product_type_id?: number | null; stock_item_id?: number | null }): Promise<{ rows: ConsumptionRow[] }> {
    const q = new URLSearchParams({ from: params.from, to: params.to });
    if (params.product_type_id) q.set("product_type_id", String(params.product_type_id));
    if (params.stock_item_id) q.set("stock_item_id", String(params.stock_item_id));
    return request(`/stock/consumption?${q.toString()}`);
  },

  async stockItemByCode(code: string): Promise<{ item: StockItem }> {
    return request(`/stock/by-code/${encodeURIComponent(code.trim())}`);
  },

  // ── Free stock issues (livrări libere) ─────────────────────────────────────
  async listTechnicians(all = false): Promise<{ technicians: Technician[] }> {
    return request(`/technicians${all ? "?all=1" : ""}`);
  },
  async createTechnician(body: { full_name: string; phone?: string | null; notes?: string | null }): Promise<{ technician: Technician }> {
    return request(`/technicians`, { method: "POST", body: JSON.stringify(body) });
  },
  async updateTechnician(
    id: number,
    body: Partial<Pick<Technician, "full_name" | "phone" | "notes" | "active">>,
  ): Promise<{ technician: Technician }> {
    return request(`/technicians/${id}`, { method: "PATCH", body: JSON.stringify(body) });
  },
  async searchIssueRecipients(q = ""): Promise<{ recipients: IssueRecipient[] }> {
    const qs = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
    return request(`/stock-issues/recipients${qs}`);
  },
  async listStockIssues(params: {
    kind?: StockIssueKind | null;
    technician_id?: number | null;
    customer_id?: number | null;
    unconfirmed?: boolean;
    /** true = doar anulate, false = doar neanulate, absent = toate */
    cancelled?: boolean | null;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ items: StockIssue[]; total: number }> {
    const q = new URLSearchParams();
    if (params.kind) q.set("kind", params.kind);
    if (params.technician_id) q.set("technician_id", String(params.technician_id));
    if (params.customer_id) q.set("customer_id", String(params.customer_id));
    if (params.unconfirmed) q.set("unconfirmed", "1");
    if (params.cancelled != null) q.set("cancelled", params.cancelled ? "1" : "0");
    if (params.limit != null) q.set("limit", String(params.limit));
    if (params.offset != null) q.set("offset", String(params.offset));
    const s = q.toString();
    return request(`/stock-issues${s ? `?${s}` : ""}`);
  },
  async getStockIssue(id: number): Promise<{ issue: StockIssue }> {
    return request(`/stock-issues/${id}`);
  },
  async createStockIssue(body: CreateStockIssueInput): Promise<{ issue: StockIssue }> {
    return request(`/stock-issues`, { method: "POST", body: JSON.stringify(body) });
  },
  async updateStockIssue(
    id: number,
    body: { lines: { stock_item_id: number; qty: number }[]; note?: string | null },
  ): Promise<{ issue: StockIssue; changes: StockIssueChange[] }> {
    return request(`/stock-issues/${id}`, { method: "PUT", body: JSON.stringify(body) });
  },
  async cancelStockIssue(id: number, body: { note?: string | null } = {}): Promise<{ issue: StockIssue }> {
    return request(`/stock-issues/${id}/cancel`, { method: "POST", body: JSON.stringify(body) });
  },
  async getHoldings(recipient?: { type: IssueRecipientType; id: number } | null): Promise<{ rows: HoldingRow[] }> {
    const qs = recipient ? `?type=${recipient.type}&id=${recipient.id}` : "";
    return request(`/stock-issues/holdings${qs}`);
  },
  async confirmIssueAccounting(id: number): Promise<{ issue: StockIssue }> {
    return request(`/stock-issues/${id}/accounting-confirm`, { method: "POST" });
  },
  async undoIssueAccounting(id: number): Promise<{ issue: StockIssue }> {
    return request(`/stock-issues/${id}/accounting-undo`, { method: "POST" });
  },
  async confirmIssueAccountingBulk(ids: number[]): Promise<{ changed: number }> {
    return request(`/stock-issues/accounting-confirm-bulk`, { method: "POST", body: JSON.stringify({ ids }) });
  },
  issueActUrl(id: number): string {
    return `${API_BASE}/stock-issues/${id}/act.pdf`;
  },

  // ── Notifications ──────────────────────────────────────────────────────────
  async listNotifications(opts?: { unread?: boolean; limit?: number }): Promise<NotificationListResponse> {
    const qs = new URLSearchParams();
    if (opts?.unread) qs.set("unread", "true");
    if (opts?.limit != null) qs.set("limit", String(opts.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return await request<NotificationListResponse>(`/notifications${suffix}`);
  },
  async markNotificationRead(id: number): Promise<void> {
    await request(`/notifications/${id}/read`, { method: "POST" });
  },
  async markAllNotificationsRead(): Promise<{ updated: number }> {
    return await request<{ updated: number }>(`/notifications/read-all`, { method: "POST" });
  },
  async getNotificationPreferences(): Promise<NotificationPreference[]> {
    const r = await request<{ preferences: NotificationPreference[] }>(`/notification-preferences`);
    return r.preferences;
  },
  async setNotificationPreference(pref: NotificationPreference): Promise<NotificationPreference> {
    const r = await request<{ preference: NotificationPreference }>(`/notification-preferences`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(pref),
    });
    return r.preference;
  },
  async markChirpstackDone(shipmentId: number): Promise<{ shipment: Shipment }> {
    return await request<{ shipment: Shipment }>(`/shipments/${shipmentId}/chirpstack-done`, { method: "POST" });
  },
  async undoChirpstackDone(shipmentId: number): Promise<{ shipment: Shipment }> {
    return await request<{ shipment: Shipment }>(`/shipments/${shipmentId}/chirpstack-done`, { method: "DELETE" });
  },
  async confirmAccounting(shipmentId: number): Promise<{ shipment: Shipment }> {
    return await request<{ shipment: Shipment }>(`/shipments/${shipmentId}/accounting-confirm`, { method: "POST" });
  },
  async unconfirmAccounting(shipmentId: number): Promise<{ shipment: Shipment }> {
    return await request<{ shipment: Shipment }>(`/shipments/${shipmentId}/accounting-confirm`, { method: "DELETE" });
  },

  // ── Public search token (settings) ──────────────────────────────────────
  getPublicSearchToken: () =>
    request<{ enabled: boolean; token: string | null; url: string | null }>("/settings/public-search"),
  generatePublicSearchToken: () =>
    request<{ enabled: boolean; token: string | null; url: string | null }>(
      "/settings/public-search/generate",
      { method: "POST" },
    ),
  revokePublicSearchToken: () =>
    request<{ enabled: boolean; token: string | null; url: string | null }>(
      "/settings/public-search/revoke",
      { method: "POST" },
    ),
};
